import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, IconBadge, ContextStrip, InfoCard, GradientButton, CTABanner, StepChip, LetterBadge } from '../../components/common/Primitives';
import ModuleCard from '../../components/common/ModuleCard';
import VideoLightbox from '../../components/common/VideoLightbox';
import { SP_OVERVIEW } from '../../lib/mockStudent';
import { FEATURED_VIDEO_ID, youtubeThumb } from '../../lib/moduleVideos';
import { Check, PlayCircle, ArrowRight, Home } from 'lucide-react';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';

export default function StudentOverview() {
  const d = SP_OVERVIEW;
  const [heroOpen, setHeroOpen] = useState(false);
  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8">
        <div className="mx-auto max-w-[1240px]">
          <div className="flex flex-wrap items-center gap-2">
            {d.hero.eyebrows.map(e => (<span key={e} className="rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold tracking-widest text-purple">{e}</span>))}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <h1 className="text-[40px] md:text-[56px] font-extrabold leading-[1.03] tracking-tight text-navy">{d.hero.title1}<br/>{d.hero.title2}</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>

              {/* No licence fee highlight — heavily emphasised per user request */}
              <div className="mt-6" data-testid="student-licence-fee-highlight"><LicenceFeeHighlight text={d.hero.pill} /></div>

              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to="/student-parent/experiences">Explore Learning</GradientButton>
                <GradientButton to="/access-request" variant="outline">Request Access</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-mint-50 p-6 md:p-8 shadow-soft">
              <button type="button" onClick={() => setHeroOpen(true)} data-testid="student-hero-video-play" aria-label="Play Student using Gyan Dhara" className="group relative block w-full aspect-video rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30">
                <img src={youtubeThumb(FEATURED_VIDEO_ID, 'maxresdefault')} alt="Student using Gyan Dhara" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 grid place-items-center"><span className="grid h-14 w-14 place-items-center rounded-full bg-white/95 shadow-soft group-hover:scale-105 transition-transform"><PlayCircle className="h-7 w-7 text-navy" /></span></span>
              </button>
              <div className="mt-4 text-[11.5px] font-bold tracking-widest text-purple">{d.hero.imageEyebrow}</div>
            </div>
          </div>
          <div className="mt-8"><ContextStrip items={d.strip} /></div>

          {/* COMPLETE LEARNING ECOSYSTEM — 4 pillars */}
          <div className="mt-6 rounded-3xl border border-canvas-200 bg-white p-6 md:p-7 shadow-soft">
            <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
              <div>
                <div className="text-[11.5px] font-bold tracking-widest text-purple">ONE COMPLETE LEARNING ECOSYSTEM</div>
                <div className="mt-1 text-[19px] md:text-[22px] font-extrabold text-navy leading-snug">VR Experiences · Web Learning Hub · AI Learning · Analytics</div>
                <p className="mt-1 max-w-2xl text-[13px] text-ink-500">Four connected layers work together to help every learner understand, practise, ask and progress.</p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { title: 'VR Experiences',     desc: 'Immersive Grade 8–9 modules for Science and Mathematics.',  color: 'purple', icon: 'Headset' },
                { title: 'Web Learning Hub',   desc: 'Chapter videos, resources and unlimited quiz practice.',    color: 'royal',  icon: 'Monitor' },
                { title: 'AI Learning',         desc: 'Gyan Mitra answers questions verbally in English and Hindi.', color: 'cyan',  icon: 'Bot' },
                { title: 'Analytics',           desc: 'Time spent, chapter progress and conceptual, applied and theoretical scores.', color: 'mint',   icon: 'BarChart3' },
              ].map((p, i) => (
                <div key={i} className="relative rounded-2xl border border-canvas-200 bg-canvas-100 p-4 transition-transform hover:-translate-y-0.5 hover:shadow-soft">
                  <IconBadge icon={p.icon} color={p.color} />
                  <div className="mt-3 text-[13.5px] font-bold text-navy">{p.title}</div>
                  <p className="mt-1 text-[12px] leading-snug text-ink-500">{p.desc}</p>
                  {i < 3 && (
                    <span className="hidden lg:flex absolute -right-2 top-1/2 -translate-y-1/2 h-6 w-6 items-center justify-center rounded-full bg-white ring-1 ring-canvas-200 text-purple">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TWO PERSPECTIVES */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.perspectives.eyebrow} title={d.perspectives.title} desc={d.perspectives.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            {[d.perspectives.students, d.perspectives.parents].map((p, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
                <div className="flex items-center gap-3">
                  <IconBadge icon={i===0?'Users':'HeartHandshake'} color={i===0?'purple':'cyan'} />
                  <div className="text-[17px] font-bold text-navy">{p.title}</div>
                </div>
                <p className="mt-3 text-[13.5px] text-ink-500 leading-relaxed">{p.desc}</p>
                <ul className="mt-5 space-y-2">{p.items.map((s,j)=>(<li key={j} className="flex items-start gap-2 text-[13.5px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {s}</li>))}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THREE WAYS */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.waysToLearn.eyebrow} title={d.waysToLearn.title} desc={d.waysToLearn.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.waysToLearn.items.map((w,i)=>(<InfoCard key={i} icon={w.icon} color={w.color} title={w.title} desc={w.desc} />))}
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.curriculumHeader.eyebrow} title={d.curriculumHeader.title} desc={d.curriculumHeader.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {d.curriculumHome.map((m,i)=>(<ModuleCard key={i} {...m} />))}
          </div>
          <div className="mt-8 flex justify-center"><GradientButton to="/student-parent/experiences" variant="outline">View All Learning Experiences <ArrowRight className="h-4 w-4" /></GradientButton></div>
        </div>
      </section>

      {/* EXPLORATION */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.explorationHeader.eyebrow} title={d.explorationHeader.title} desc={d.explorationHeader.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {d.explorationHome.map((m,i)=>(<ModuleCard key={i} {...m} />))}
          </div>
          <div className="mt-8 flex justify-center"><GradientButton to="/student-parent/experiences" variant="outline">View All Learning Experiences <ArrowRight className="h-4 w-4" /></GradientButton></div>
        </div>
      </section>

      {/* LEARNING HUB TEASER */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.learningHubTeaser.eyebrow} title={d.learningHubTeaser.title} desc={d.learningHubTeaser.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.35fr_1fr]">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {d.learningHubTeaser.items.map((c,i)=>(<InfoCard key={i} icon={c.icon} color={c.color} title={c.title} desc={c.desc} />))}
            </div>
            <div className="rounded-2xl bg-navy p-6 text-white shadow-card">
              <div className="text-[11.5px] font-bold tracking-widest text-cyan">DASHBOARD PREVIEW</div>
              <div className="mt-2 text-[18px] font-bold">{d.learningHubTeaser.dashboard.title}</div>
              <p className="mt-1 text-[12.5px] text-white/70">{d.learningHubTeaser.dashboard.desc}</p>
              <div className="mt-4 space-y-3">
                {d.learningHubTeaser.dashboard.rows.map((r,i)=>(<div key={i} className="rounded-xl bg-white/5 p-3 border border-white/10"><div className="text-[10.5px] font-bold tracking-widest text-cyan">{r.k}</div><div className="mt-1 text-[13.5px] font-semibold">{r.v}</div></div>))}
              </div>
              <button className="mt-5 w-full rounded-lg bg-white px-4 py-2 text-[13px] font-semibold text-navy hover:bg-canvas-100 transition-colors">{d.learningHubTeaser.dashboard.cta}</button>
            </div>
          </div>
        </div>
      </section>

      {/* INDIVIDUAL JOURNEY */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.journey.eyebrow} title={d.journey.title} desc={d.journey.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {d.journey.steps.map((s,i)=>(
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft flex items-start gap-3">
                <StepChip n={s.n} color={['purple','royal','cyan','mint','purple','royal'][i]} />
                <div><div className="text-[14.5px] font-bold text-navy">{s.title}</div><p className="mt-1 text-[12.5px] text-ink-500">{s.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRESS */}
      <section id="progress" className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.progress.eyebrow} title={d.progress.title} desc={d.progress.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="text-[15px] font-bold text-navy">{d.progress.overviewTitle}</div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {d.progress.metrics.map((m,i)=>(<div key={i} className="rounded-xl bg-canvas-100 p-4"><div className="text-[10.5px] font-bold tracking-widest text-purple">{m.label}</div><div className="mt-2 text-[22px] font-extrabold text-navy">{m.k}</div></div>))}
              </div>
              <div className="mt-6">
                <div className="text-[13px] font-bold text-navy">Performance by learning type</div>
                <div className="mt-3 space-y-3">
                  {d.progress.performance.map((p,i)=>(
                    <div key={i}><div className="flex items-center justify-between text-[12.5px]"><span className="font-semibold text-ink-700">{p.label}</span><span className="font-bold text-navy">{p.v}%</span></div><div className="mt-1 h-2 rounded-full bg-canvas-200 overflow-hidden"><div className={`h-full bg-gradient-to-r ${i===0?'from-purple to-royal':i===1?'from-royal to-cyan':'from-cyan to-mint'}`} style={{ width: `${p.v}%` }} /></div></div>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="text-[15px] font-bold text-navy">Parent-ready insights</div>
              <ul className="mt-4 space-y-2">{d.progress.parentRows.map((r,i)=>(<li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {r}</li>))}</ul>
              <div className="mt-5 rounded-lg bg-canvas-100 p-3 text-[12px] text-ink-500">{d.progress.note}</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAMILY ACCESS TEASER (moved above comfort) */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.familyTeaser.eyebrow} title={d.familyTeaser.title} desc={d.familyTeaser.desc} />
          <div className="mt-8 rounded-2xl border border-canvas-200 bg-canvas-100 p-6 shadow-soft">
            <div className="flex flex-col md:flex-row md:items-start gap-5">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50 text-purple"><Home className="h-6 w-6" /></span>
              <div className="flex-1">
                <div className="text-[17px] font-bold text-navy">{d.familyTeaser.card.title}</div>
                <p className="mt-1 text-[13.5px] text-ink-500">{d.familyTeaser.card.desc}</p>
                <ul className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-3">{d.familyTeaser.card.items.map((s,i)=>(<li key={i} className="flex items-start gap-2 text-[12.5px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {s}</li>))}</ul>
              </div>
              <GradientButton to="/student-parent/access" variant="outline">Family Access <ArrowRight className="h-4 w-4" /></GradientButton>
            </div>
          </div>
        </div>
      </section>

      {/* COMFORT */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.comfort.eyebrow} title={d.comfort.title} desc={d.comfort.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {d.comfort.items.map((c,i)=>(
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft text-center">
                <div className={`mx-auto grid h-14 w-14 place-items-center rounded-full font-bold ${['bg-purple-50 text-purple','bg-royal-50 text-royal','bg-cyan-50 text-cyan','bg-mint-50 text-mint'][i]}`}>{c.badge}</div>
                <div className="mt-3 text-[14px] font-bold text-navy">{c.title}</div>
                <p className="mt-1 text-[12px] text-ink-500">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOFTWARE ACCESS */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <div className="rounded-3xl border border-mint-50 bg-gradient-to-br from-mint-50/60 via-white to-royal-50/40 p-6 md:p-10 shadow-soft">
            <h3 className="text-[26px] md:text-[30px] font-extrabold text-navy">{d.access.title}</h3>
            <p className="mt-3 max-w-3xl text-[14px] text-ink-700">{d.access.included}</p>
            <p className="mt-2 max-w-3xl text-[14px] text-ink-500">{d.access.separate}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <GradientButton to="/access-request">Request Student or Family Access</GradientButton>
              <GradientButton to="/student-parent/experiences" variant="outline">Explore All Experiences</GradientButton>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <div className="text-left"><Eyebrow color="purple">{d.cta.eyebrow}</Eyebrow><h3 className="mt-2 text-[26px] md:text-[30px] font-extrabold text-navy">{d.cta.kicker}</h3><p className="mt-3 max-w-xl text-[14px] text-ink-500">{d.cta.desc}</p></div>
          <div className="mt-8"><CTABanner title={d.cta.title} desc={d.cta.subtitle} primary={{ label: 'Request Access', to: '/access-request' }} secondary={{ label: 'Explore Experiences', to: '/student-parent/experiences' }} /></div>
        </div>
      </section>

      {heroOpen && <VideoLightbox videoId={FEATURED_VIDEO_ID} title="Student using Gyan Dhara" onClose={() => setHeroOpen(false)} />}
    </PageShell>
  );
}
